import { useState } from 'react'
import { api } from '../api/client'
import type { SessionInfo } from '../api/types'
import { Banner } from '../components/Banner'

interface Props {
  onCreated: (session: SessionInfo) => void
  /** Continue with the deployment open, as it was before this screen existed. */
  onSkip: () => void
}

const MINIMUM_PASSWORD = 8

/**
 * First run: no accounts exist, so there is nobody to sign in as.
 *
 * This screen is only reachable in that state, and creating the account closes it —
 * along with the rest of the API, which until this moment answers anyone who can reach
 * the port. That is the honest framing, so the form says it rather than presenting
 * itself as a neutral welcome.
 */
export function Setup({ onCreated, onSkip }: Props) {
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const tooShort = password.length > 0 && password.length < MINIMUM_PASSWORD
  const mismatch = repeat.length > 0 && password !== repeat
  const ready = username.trim().length > 0 && password.length >= MINIMUM_PASSWORD && password === repeat

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      await api.setup(username.trim(), password, displayName.trim())
      // The setup call signs the new administrator in, so read the session back rather
      // than assuming what it contains.
      onCreated(await api.session())
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Could not create the account.')
      setBusy(false)
    }
  }

  return (
    <div className="login">
      <form className="card login__card" onSubmit={submit}>
        <h1 className="login__title">Set up GA4 Briefing</h1>
        <p className="login__hint">
          This deployment has no accounts yet, so it is currently readable by anyone who can
          reach it. Creating the first administrator closes it.
        </p>

        {error && <Banner tone="critical">{error}</Banner>}

        <label className="field">
          <span className="field__label">Username</span>
          <input
            name="username"
            autoComplete="username"
            autoFocus
            required
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Display name (optional)</span>
          <input
            name="displayName"
            autoComplete="name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Password</span>
          <input
            name="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <span className="field__note">
            {tooShort ? `At least ${MINIMUM_PASSWORD} characters.` : `${MINIMUM_PASSWORD} characters or more.`}
          </span>
        </label>

        <label className="field">
          <span className="field__label">Repeat password</span>
          <input
            name="repeatPassword"
            type="password"
            autoComplete="new-password"
            required
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
          />
          {mismatch && <span className="field__note field__note--error">The passwords do not match.</span>}
        </label>

        <button className="button" type="submit" disabled={busy || !ready}>
          {busy ? 'Creating…' : 'Create administrator'}
        </button>

        {/* An existing deployment upgraded into this screen was working fine a minute
            ago. Blocking it behind a form it never asked for would be a regression. */}
        <button type="button" className="login__skip" onClick={onSkip}>
          Skip for now and leave it open
        </button>
      </form>
    </div>
  )
}
